import React from "react";

import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";

interface SidebarContentProps {
  sidebarContent: any[];
  selectedItem?: string;
  onSelectItem?: (name: string) => void;
  /** Show app logo above the back button (short-video / video editor only) */
  showLogo?: boolean;
}

export function SidebarContent({
  sidebarContent,
  selectedItem,
  onSelectItem,
  showLogo = false,
}: SidebarContentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <List
      component="nav"
      sx={{
        height: "100%",
        backgroundColor: (themeMode) => themeMode.palette.background.paper,
        borderRight: (themeMode) => `1px solid ${themeMode.palette.divider}`,
      }}
    >
      <Box>
        {/* Desktop: logo (video editor only), then back to short videos */}
        {!isMobile && (
          <div>
            {showLogo && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  px: 1,
                  pb: 3,
                }}
              >
                <img
                  src="/logo.png"
                  alt="Podcast Assistant"
                  style={{
                    width: 36,
                    height: 36,
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </div>
        )}
        {sidebarContent.map((item: any) => {
          const isSelected = selectedItem === item.name;
          return (
            <Box key={item.name}>
              {item.disabled ? (
                <Tooltip
                  title="Coming Soon"
                  placement="right"
                  arrow
                  sx={{
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#1976d2",
                    },
                  }}
                >
                  <Box>
                    <ListItemButton
                      disabled={item.disabled}
                      sx={{
                        px: 1,
                        paddingBottom: "12px",
                        paddingTop: "12px",
                        backgroundColor: isSelected
                          ? theme.palette.action.selected
                          : "",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: "8px",
                        },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyItems: "center",
                        cursor: "not-allowed",
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText>
                        <Typography sx={{ fontSize: "10px" }}>
                          {item.name}
                        </Typography>
                      </ListItemText>
                    </ListItemButton>
                  </Box>
                </Tooltip>
              ) : (
                <ListItemButton
                  onClick={() => onSelectItem?.(item.name)}
                  sx={{
                    px: 1,
                    paddingBottom: "12px",
                    paddingTop: "12px",
                    backgroundColor: isSelected
                      ? theme.palette.action.selected
                      : "",
                    borderLeft: isSelected
                      ? `3px solid ${theme.palette.primary.main}`
                      : "3px solid transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: "8px",
                    },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>
                    <Typography sx={{ fontSize: "10px" }}>
                      {item.name}
                    </Typography>
                  </ListItemText>
                </ListItemButton>
              )}
            </Box>
          );
        })}
      </Box>
    </List>
  );
}
